import React, { useState, useRef, useEffect } from 'react';
import { DollarSign, Calendar, Users, Globe, CheckCircle, ArrowRight, Sparkles, TrendingUp, Award, Star, Zap, Target, ShieldCheck } from 'lucide-react';

const stats = [
  { value: '$900K+', label: 'оборот в год', icon: DollarSign, color: 'amber', desc: 'Подтверждённый доход' },
  { value: '5 лет', label: 'на Amazon', icon: Calendar, color: 'emerald', desc: 'С 2020 года' },
  { value: '7', label: 'человек в команде', icon: Users, color: 'blue', desc: 'Полноценный бизнес' },
  { value: 'EU & USA', label: 'рынки продаж', icon: Globe, color: 'violet', desc: 'Оба направления' },
];

const colorMap = {
  amber: { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/40', shadow: 'shadow-amber-500/30' },
  emerald: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/40', shadow: 'shadow-emerald-500/30' },
  blue: { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/40', shadow: 'shadow-blue-500/30' },
  violet: { gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/40', shadow: 'shadow-violet-500/30' },
};

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredStat, setHoveredStat] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 30,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 30
    });
  };

  return (
    <section 
      ref={sectionRef}
      id="about" 
      className="relative py-20 sm:py-28 overflow-hidden bg-[#030304]"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-1/4 w-[800px] h-[800px] bg-amber-500/[0.04] rounded-full blur-[200px] animate-blob" />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-violet-500/[0.03] rounded-full blur-[150px] animate-blob" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-6 backdrop-blur-xl">
            <Award className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 font-bold text-sm tracking-wide">АВТОР КУРСА</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-white">Кто проведёт вас к </span>
            <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">первым продажам</span>
          </h2>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left: Photo with 3D effect */}
          <div 
            className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setMousePosition({ x: 0, y: 0 })}
          >
            <div className="relative max-w-lg mx-auto lg:mx-0" ref={cardRef}>
              {/* Animated glow rings */}
              <div className="absolute -inset-8 bg-gradient-to-br from-amber-500/30 via-orange-500/20 to-violet-500/20 rounded-[3rem] blur-3xl animate-pulse" />
              <div className="absolute -inset-4 bg-gradient-to-br from-amber-500/20 to-transparent rounded-[3rem] blur-2xl" />
              
              {/* 3D Card */}
              <div 
                className="relative rounded-3xl overflow-hidden border-2 border-amber-500/40 bg-zinc-900/80 backdrop-blur-xl shadow-2xl shadow-amber-500/20 transition-transform duration-300 ease-out"
                style={{ 
                  transform: `perspective(1000px) rotateY(${mousePosition.x * 0.3}deg) rotateX(${-mousePosition.y * 0.3}deg)`,
                }}
              >
                {/* Shine effect */}
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    transform: `translateX(${mousePosition.x * 3}px) translateY(${mousePosition.y * 3}px)`
                  }}
                />
                
                {/* Top gradient bar */}
                <div className="h-2 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 animate-gradient bg-[length:200%_auto]" />
                
                {/* Image */}
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_amazon-course-1/artifacts/6vupjb0q_image.png"
                    alt="Вадим Малюк"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent" />
                </div>
                
                {/* Name card */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl blur-xl" />
                    <div className="relative p-5 rounded-2xl bg-zinc-900/90 backdrop-blur-xl border border-amber-500/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-4 h-4 rounded-full bg-emerald-500" />
                            <div className="absolute inset-0 w-4 h-4 rounded-full bg-emerald-500 animate-ping opacity-50" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-black text-white">Вадим Малюк</h3>
                            <p className="text-amber-400 font-semibold">Amazon-продавец с 2020</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30">
                          <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                          <span className="text-amber-400 font-black">5 лет</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating badge */}
                <div className="absolute top-6 right-6">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl blur-lg opacity-50 group-hover:opacity-80 transition-opacity" />
                    <div className="relative px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black shadow-xl">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        <span>С 2020</span>
                      </div>
                      <p className="text-emerald-100 text-xs font-medium">на Amazon</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Stats + Info */}
          <div className={`space-y-8 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            
            {/* Stats Grid - MEGA */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, i) => {
                const Icon = stat.icon;
                const colors = colorMap[stat.color];
                const isHovered = hoveredStat === i;
                
                return (
                  <div
                    key={i}
                    className="group relative"
                    onMouseEnter={() => setHoveredStat(i)}
                    onMouseLeave={() => setHoveredStat(null)}
                  >
                    {/* Glow */}
                    <div className={`absolute -inset-2 bg-gradient-to-r ${colors.gradient} rounded-2xl blur-xl transition-opacity duration-500 ${
                      isHovered ? 'opacity-30' : 'opacity-0'
                    }`} />
                    
                    <div className={`relative p-5 rounded-2xl border-2 transition-all duration-500 overflow-hidden cursor-default ${
                      isHovered 
                        ? `${colors.bg} ${colors.border} -translate-y-2 shadow-2xl ${colors.shadow}` 
                        : 'bg-zinc-900/50 border-zinc-800/60 hover:border-zinc-700'
                    }`}>
                      
                      {/* Top accent */}
                      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${colors.gradient} transition-transform duration-500 origin-left ${isHovered ? 'scale-x-100' : 'scale-x-0'}`} />
                      
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-3 transition-all duration-500 ${
                        isHovered ? `bg-gradient-to-br ${colors.gradient} shadow-lg ${colors.shadow} rotate-3 scale-110` : 'bg-zinc-800'
                      }`}>
                        <Icon className={`w-7 h-7 transition-colors duration-300 ${isHovered ? 'text-white' : colors.text}`} />
                      </div>
                      
                      <p className={`text-4xl font-black mb-1 transition-colors duration-300 ${isHovered ? 'text-white' : 'text-zinc-100'}`}>
                        {stat.value}
                      </p>
                      <p className={`text-sm font-semibold mb-1 transition-colors duration-300 ${isHovered ? colors.text : 'text-zinc-500'}`}>
                        {stat.label}
                      </p>
                      <p className={`text-xs transition-all duration-300 ${isHovered ? 'text-zinc-400 opacity-100' : 'text-zinc-600 opacity-0'}`}>
                        {stat.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bio - Premium */}
            <div className="relative p-6 rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 backdrop-blur-xl border border-zinc-700/50 rounded-2xl" />
              
              <div className="relative space-y-4">
                {[
                  { icon: Zap, color: 'amber', text: 'Начал с анализа товаров и работы ресёрчером' },
                  { icon: Target, color: 'emerald', text: 'Запустил собственный бизнес на Amazon' },
                  { icon: TrendingUp, color: 'violet', text: 'Сегодня — продажи в США и Европе, команда из 7 человек' },
                ].map((item, i) => {
                  const colors = colorMap[item.color];
                  return (
                    <div key={i} className="flex items-start gap-4 group">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${colors.gradient} flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-lg text-zinc-300 leading-relaxed group-hover:text-white transition-colors">
                        {item.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* What's in training - MEGA */}
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-3xl blur-2xl" />
              
              <div className="relative p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border-2 border-amber-500/30 backdrop-blur-xl">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <ShieldCheck className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-xl font-black text-white">В обучении:</h4>
                </div>
                
                <div className="space-y-3">
                  {['Примеры из реального бизнеса', 'Реальные товары и поставщики', 'Разбор типичных ошибок'].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-zinc-900/50 border border-zinc-800/50 hover:border-emerald-500/30 transition-colors group">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-white font-semibold text-lg">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA - MEGA */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 rounded-2xl blur-lg opacity-70 group-hover:opacity-100 transition-opacity duration-300 animate-pulse" />
              
              <a 
                href="#pricing"
                className="relative flex items-center justify-center gap-4 w-full px-8 py-5 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 bg-[length:200%_auto] animate-gradient text-black font-black text-xl rounded-2xl shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 active:scale-[0.98] transition-all duration-300 overflow-hidden"
                style={{ touchAction: 'manipulation' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Zap className="w-6 h-6" />
                <span>Начать обучение</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
